// @flow

import React from 'react';
import cx from 'classnames';
import Translate from 'client/components/translate';
import 'client/styles/form.scss';
import type { TranslateType } from 'types/translateType.flow';

type ValueType = string | number | null;

export type OptionType = {
  value: ValueType,
  label: string,
  disabled: boolean,
};

const DEFAULT_VALUE = '';

function renderOption(isRequired) {
  // eslint-disable-next-line react/prop-types
  return ({ value, label, disabled = false }: OptionType, index) =>
    (index === 0 && isRequired && (value === null || value === '') ? (
      <option disabled={disabled} value={value} key={value}>
        {label}
      </option>
    ) : (
      <option disabled={disabled} value={value} key={value}>
        {label}
      </option>
    ));
}

export type SelectProps = {
  alwaysDisplayLabel?: boolean,
  changeHandler: Function,
  disabled: boolean,
  error: TranslateType | false | null,
  id: string,
  info: Object | null,
  infoText: string | null,
  isRequired: boolean,
  label: TranslateType | null,
  options: Array<OptionType>,
  optionsMap: Function,
  optionsPath: string | null,
  placeholder: string | null,
  suppressError: boolean,
  value: ValueType,
};

type MakeOptionsArgType = {
  options: Array<OptionType>,
  placeholder: string | null,
  isRequired: boolean,
  optionsPath: string | null,
  optionsMap: Function,
};

function makeOptions(props: MakeOptionsArgType) {
  const { options, placeholder, isRequired, optionsPath, optionsMap } = props;
  const finalOptions = [];
  if (placeholder) {
    finalOptions.push({
      value: DEFAULT_VALUE,
      label: placeholder,
    });
  }

  if (optionsPath) {
    return (
      <Translate path={optionsPath}>
        {(data) => {
          const mappedData = optionsMap(data);
          return (
            <optgroup>{finalOptions.concat(mappedData).map(renderOption(isRequired))}</optgroup>
          );
        }}
      </Translate>
    );
  }

  return finalOptions.concat(options).map(renderOption(isRequired));
}

export default class Select extends React.PureComponent<SelectProps> {
  static defaultProps = {
    alwaysDisplayLabel: false,
    options: [],
    optionsPath: null,
    optionsMap: (d: any) => d,
    value: null,
    label: null,
    isRequired: false,
    error: null,
    disabled: false,
    suppressError: false,
    info: null,
    infoText: null,
    placeholder: null,
  };

  render() {
    const {
      alwaysDisplayLabel,
      changeHandler,
      disabled,
      error,
      id,
      info,
      infoText,
      isRequired,
      label,
      options,
      optionsMap,
      optionsPath,
      placeholder,
      suppressError,
      value,
      ...props
    } = this.props;

    return (
      <div className={cx('select-component', { error })}>
        {!!label && (
          <div
            className={cx('label-container', {
              'show-label': value !== DEFAULT_VALUE || alwaysDisplayLabel,
            })}>
            <label htmlFor={id} id={`${id}-label`}>
              <Translate {...label} />
            </label>
          </div>
        )}
        <div className={cx('select-container flex-row', { disabled })}>
          <select
            {...props}
            disabled={disabled}
            name={id}
            id={id}
            value={value}
            onChange={(e) => {
              changeHandler(e.target.value);
            }}
            className={cx({
              'default-selected': value === DEFAULT_VALUE,
            })}>
            {makeOptions({ placeholder, isRequired, options, optionsPath, optionsMap })}
          </select>
          <div className="angle-container flex-row">
            <div className="icon-down-angle" />
          </div>
        </div>
        {error && !suppressError ? (
          <div className="error-message">{error && !suppressError && <Translate {...error} />}</div>
        ) : (
          <div className="info-message">
            {(infoText || info) && (infoText || <Translate {...info} />)}
          </div>
        )}
      </div>
    );
  }
}
